<template>
  <div class="learning-records">
    <header class="workspace-header">
      <div class="workspace-heading">
        <div class="workspace-title-row">
          <h1 class="workspace-title">{{ copy.title }}</h1>
          <span class="workspace-count">{{ copy.recordCount(records.length) }} · {{ copy.attemptCountInline(overall.attemptCount || 0) }}</span>
        </div>
        <p class="workspace-subtitle">{{ copy.subtitle }}</p>
      </div>
    </header>

    <div v-if="loading" class="workspace-state">{{ copy.loading }}</div>
    <div v-else-if="error" class="workspace-state error">
      <span>{{ error }}</span>
      <button type="button" @click="loadRecords">{{ copy.retry }}</button>
    </div>
    <div v-else-if="records.length === 0" class="workspace-state">
      {{ copy.empty }}
    </div>

    <template v-else>
      <div class="records-layout">
        <aside class="practice-list" aria-label="Practice list">
          <button
            v-for="practice in records"
            :key="practice.practiceId"
            type="button"
            class="practice-row"
            :class="{ active: practice.practiceId === selectedPracticeId }"
            :aria-pressed="practice.practiceId === selectedPracticeId"
            @click="selectPractice(practice.practiceId)"
          >
            <span class="practice-main">
              <strong>{{ practice.audioName || copy.untitled }}</strong>
              <small>{{ formatDate(practice.lastPracticedAt) }}</small>
            </span>
            <span class="practice-score" :class="scoreLevel(averageScore(practice))">
              {{ scoreLabel(averageScore(practice)) }}
            </span>
          </button>
        </aside>

        <section v-if="selectedPractice" class="report-panel">
          <header class="report-header">
            <div class="report-heading">
              <p class="records-kicker">{{ sourceLabel(selectedPractice) }}</p>
              <h2>{{ selectedPractice.audioName || copy.untitled }}</h2>
              <p class="report-date">{{ formatDate(selectedPractice.lastPracticedAt) }}</p>
            </div>
            <div class="report-title-actions">
              <button
                type="button"
                class="report-open"
                :disabled="Boolean(openingPracticeId)"
                @click="openPractice(selectedPractice)"
              >
                <span class="material-icons-outlined" aria-hidden="true">play_arrow</span>
                {{ openingPracticeId === selectedPractice.practiceId ? copy.openingAudio : copy.openAudio }}
              </button>
              <button type="button" class="report-delete" @click="requestDelete(selectedPractice)">
                <span class="material-icons-outlined" aria-hidden="true">delete_outline</span>
                {{ copy.deleteRecord }}
              </button>
            </div>
          </header>
          <p v-if="actionError" class="action-error">{{ actionError }}</p>

          <section class="performance-overview">
            <div class="overall-card">
              <div class="score-orbit" :style="{ '--score-progress': scoreDegrees(overallScore) }">
                <div class="score-orbit-inner">
                  <span>{{ copy.overallScore }}</span>
                  <strong>{{ scoreLabel(overallScore) }}</strong>
                  <small>{{ copy.scoreTier(overallScore) }}</small>
                </div>
              </div>
              <div class="practice-volume">
                <span>
                  <strong>{{ selectedStats.attemptCount || 0 }}</strong>
                  {{ copy.ratedAttempts }}
                </span>
                <span>
                  <strong>{{ selectedStats.practicedSentenceCount || 0 }}</strong>
                  {{ copy.practicedSentences }}
                </span>
              </div>
            </div>

            <div class="metric-board">
              <div class="section-heading">
                <span>{{ copy.performanceTitle }}</span>
                <small>{{ copy.performanceSubtitle }}</small>
              </div>
              <div v-for="field in scoreFields" :key="field.key" class="metric-row">
                <span class="metric-label">{{ field.label }}</span>
                <div class="metric-track" aria-hidden="true">
                  <span :style="{ width: scorePercent(field.value) }"></span>
                </div>
                <strong>{{ scoreLabel(field.value) }}</strong>
              </div>
            </div>
          </section>

          <div v-if="detailLoading" class="detail-skeleton" aria-live="polite">
            <span class="skeleton-label">{{ copy.loadingDetail }}</span>
            <div class="skeleton-grid"><i></i><i></i></div>
            <i class="skeleton-wide"></i>
          </div>
          <div v-else-if="detailError" class="detail-error">
            <span>{{ detailError }}</span>
            <button type="button" @click="loadPracticeDetail(selectedPracticeId)">{{ copy.retry }}</button>
          </div>
          <template v-else>
            <div class="diagnostic-grid">
              <section class="diagnostic-card problem-card">
                <div class="section-heading">
                  <span>{{ copy.problemWords }}</span>
                  <small>{{ copy.problemWordsHint }}</small>
                </div>
                <div v-if="problemWords.length" class="word-cloud">
                  <span v-for="word in problemWords" :key="word.word" class="word-chip">
                    {{ word.word }}
                    <small>{{ word.count }}</small>
                  </span>
                </div>
                <p v-else class="muted">{{ copy.noProblemWords }}</p>
              </section>

              <section class="diagnostic-card focus-card">
                <div class="section-heading">
                  <span>{{ copy.focusTitle }}</span>
                  <small>{{ copy.focusSubtitle }}</small>
                </div>
                <template v-if="weakestMetric">
                  <div class="focus-score">
                    <strong>{{ scoreLabel(weakestMetric.value) }}</strong>
                    <span>{{ weakestMetric.label }}</span>
                  </div>
                  <p>{{ copy.metricAdvice[weakestMetric.key] }}</p>
                </template>
                <p v-else class="muted">{{ copy.focusEmpty }}</p>
              </section>
            </div>

            <section class="weak-section">
              <div class="section-heading">
                <span>{{ copy.weakSentences }}</span>
                <small>{{ copy.weakSentencesHint }}</small>
              </div>
              <div v-if="weakSentences.length" class="sentence-list">
                <div v-for="sentence in weakSentences" :key="sentence.subtitleIndex" class="sentence-row">
                  <span class="sentence-index">{{ copy.sentenceNumber(Number(sentence.subtitleIndex) + 1) }}</span>
                  <p>{{ sentence.referenceText }}</p>
                  <strong :class="scoreLevel(sentence.pronunciationScore)">
                    {{ scoreLabel(sentence.pronunciationScore) }}
                  </strong>
                </div>
              </div>
              <p v-else class="muted">{{ copy.noWeakSentences }}</p>
            </section>
          </template>
        </section>
      </div>
    </template>

    <div v-if="practiceToDelete" class="delete-dialog" role="dialog" aria-modal="true" :aria-label="copy.deleteRecord">
      <button class="delete-dialog-backdrop" type="button" :aria-label="copy.cancel" @click="cancelDelete"></button>
      <div class="delete-dialog-card">
        <h3>{{ copy.deleteTitle }}</h3>
        <p>{{ copy.deleteMessage(practiceToDelete.audioName || copy.untitled) }}</p>
        <div class="delete-dialog-actions">
          <button type="button" class="dialog-cancel" :disabled="deleting" @click="cancelDelete">
            {{ copy.cancel }}
          </button>
          <button type="button" class="dialog-delete" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? copy.deleting : copy.deleteRecord }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiPost } from '@/composables/useApi'
import { resolveStudySource, StudySourceMissingError, StudySourceNotReadyError } from '@/composables/studySource'
import { useViews } from '@/composables/views'

const copyByLocale = {
  zh: {
    kicker: '跟读记录',
    title: '跟读记录',
    subtitle: '集中查看每个音频的薄弱句子、高频问题词和发音维度表现。',
    loading: '正在加载学习记录...',
    retry: '重试',
    empty: '还没有跟读评分记录。完成一次智能跟读后，这里会自动生成报告。',
    recordCount: (count) => `${count} 个项目`,
    attemptCountInline: (count) => `${count} 次跟读`,
    untitled: '未命名音频',
    problemWords: '高频问题词',
    weakSentences: '薄弱句子',
    noProblemWords: '暂时没有明显问题词。',
    noWeakSentences: '暂时没有明显薄弱句。',
    fileSource: '我的文件',
    materialSource: '素材练习',
    openAudio: '打开音频',
    openingAudio: '正在打开...',
    deleteRecord: '删除记录',
    deleteTitle: '删除跟读记录',
    deleteMessage: (name) => `确定删除“${name}”的全部跟读记录吗？`,
    cancel: '取消',
    deleting: '删除中...',
    audioUnavailable: '音频已不存在，这条跟读记录已同步删除。',
    openFailed: '无法打开这个音频。',
    deleteFailed: '删除跟读记录失败。',
    overallScore: '综合评分',
    scoreTier: (score) => score >= 85 ? '表现出色' : score >= 70 ? '状态良好' : score >= 50 ? '仍可提升' : '重点练习',
    ratedAttempts: '次评分',
    practicedSentences: '个练习句子',
    performanceTitle: '发音维度',
    performanceSubtitle: '最近一次各句评分的平均表现',
    loadingDetail: '正在加载发音诊断...',
    problemWordsHint: '出现频率较高的发音问题',
    focusTitle: '优先提升',
    focusSubtitle: '当前得分最低的发音维度',
    focusEmpty: '完成更多跟读后，这里会给出练习重点。',
    weakSentencesHint: '建议优先回听并重新跟读',
    sentenceNumber: (index) => `第 ${index} 句`,
    metricAdvice: {
      accuracyScore: '放慢语速，逐词对照原音，重点校准辅音和元音位置。',
      fluencyScore: '先按意群练习停顿，再逐步连成整句，保持节奏连续。',
      completenessScore: '完整说完每个词，留意句尾和轻读词，减少漏词。',
    },
  },
  en: {
    kicker: 'Learning Records',
    title: 'Shadowing Score Report',
    subtitle: 'Review weak sentences, recurring problem words, and pronunciation dimensions by audio.',
    loading: 'Loading learning records...',
    retry: 'Retry',
    empty: 'No pronunciation records yet. Finish one smart shadowing attempt to generate a report.',
    recordCount: (count) => `${count} items`,
    attemptCountInline: (count) => `${count} attempts`,
    untitled: 'Untitled audio',
    problemWords: 'Problem words',
    weakSentences: 'Weak sentences',
    noProblemWords: 'No obvious problem words yet.',
    noWeakSentences: 'No obvious weak sentences yet.',
    fileSource: 'My file',
    materialSource: 'Material practice',
    openAudio: 'Open audio',
    openingAudio: 'Opening...',
    deleteRecord: 'Delete record',
    deleteTitle: 'Delete shadowing record',
    deleteMessage: (name) => `Delete all shadowing records for “${name}”?`,
    cancel: 'Cancel',
    deleting: 'Deleting...',
    audioUnavailable: 'The audio no longer exists. Its shadowing record has been removed.',
    openFailed: 'Unable to open this audio.',
    deleteFailed: 'Failed to delete the shadowing record.',
    overallScore: 'Overall score',
    scoreTier: (score) => score >= 85 ? 'Excellent' : score >= 70 ? 'On track' : score >= 50 ? 'Keep improving' : 'Needs focus',
    ratedAttempts: 'rated attempts',
    practicedSentences: 'sentences practiced',
    performanceTitle: 'Pronunciation dimensions',
    performanceSubtitle: 'Average of the latest sentence scores',
    loadingDetail: 'Loading pronunciation diagnosis...',
    problemWordsHint: 'Recurring pronunciation issues',
    focusTitle: 'Priority focus',
    focusSubtitle: 'Your lowest-scoring dimension',
    focusEmpty: 'Complete more attempts to see a practice focus.',
    weakSentencesHint: 'Replay and repeat these first',
    sentenceNumber: (index) => `Sentence ${index}`,
    metricAdvice: {
      accuracyScore: 'Slow down and compare each word with the model, especially vowel and consonant placement.',
      fluencyScore: 'Practice in thought groups, then connect them while keeping a steady rhythm.',
      completenessScore: 'Finish every word and pay attention to sentence endings and unstressed words.',
    },
  },
}

export default {
  name: 'LearningRecords',
  data() {
    return {
      loading: false,
      error: '',
      records: [],
      overall: {},
      selectedPracticeId: null,
      openingPracticeId: null,
      actionError: '',
      practiceToDelete: null,
      deleting: false,
      detailLoading: false,
      detailError: '',
      detailPracticeId: null,
      selectedPracticeDetail: null,
      detailRequestId: 0,
    }
  },
  setup() {
    const { handleViewChange } = useViews()
    return {
      _handleViewChange: handleViewChange,
    }
  },
  computed: {
    copy() {
      return this.$i18n?.locale === 'en' ? copyByLocale.en : copyByLocale.zh
    },
    selectedPractice() {
      return this.records.find((record) => record.practiceId === this.selectedPracticeId) || this.records[0] || null
    },
    selectedReport() {
      return this.detailPracticeId === this.selectedPracticeId
        ? this.selectedPracticeDetail?.report || {}
        : {}
    },
    selectedStats() {
      return this.detailPracticeId === this.selectedPracticeId
        ? this.selectedPracticeDetail?.practice?.stats || this.selectedPractice?.stats || {}
        : this.selectedPractice?.stats || {}
    },
    selectedAverages() {
      return this.selectedReport.latestAverageScores || this.selectedPractice?.stats?.averageScores || {}
    },
    scoreFields() {
      return [
        {
          key: 'accuracyScore',
          label: this.$t('audioPlayer.pronunciationAssessment.accuracy'),
          value: this.selectedAverages.accuracyScore,
        },
        {
          key: 'fluencyScore',
          label: this.$t('audioPlayer.pronunciationAssessment.fluency'),
          value: this.selectedAverages.fluencyScore,
        },
        {
          key: 'completenessScore',
          label: this.$t('audioPlayer.pronunciationAssessment.completeness'),
          value: this.selectedAverages.completenessScore,
        },
      ]
    },
    problemWords() {
      return this.selectedReport.problemWords || []
    },
    weakSentences() {
      return this.selectedReport.weakSentences || []
    },
    overallScore() {
      return this.selectedAverages.pronunciationScore ?? this.averageScore(this.selectedPractice)
    },
    weakestMetric() {
      return this.scoreFields
        .filter((field) => Number.isFinite(Number(field.value)))
        .sort((a, b) => Number(a.value) - Number(b.value))[0] || null
    },
  },
  async mounted() {
    await this.loadRecords()
  },
  methods: {
    async loadRecords() {
      this.loading = true
      this.error = ''
      try {
        const result = await apiPost('/api/pronunciation-practice/list', { limit: 60 })
        this.records = result.practices || []
        this.overall = result.summary || {}
        if (!this.selectedPracticeId || !this.records.some((record) => record.practiceId === this.selectedPracticeId)) {
          this.selectedPracticeId = this.records[0]?.practiceId || null
        }
      } catch (error) {
        this.error = error.message || 'Failed to load learning records'
      } finally {
        this.loading = false
      }
      if (this.selectedPracticeId) {
        this.loadPracticeDetail(this.selectedPracticeId)
      }
    },
    selectPractice(practiceId) {
      if (!practiceId || practiceId === this.selectedPracticeId) return
      this.selectedPracticeId = practiceId
      this.actionError = ''
      this.loadPracticeDetail(practiceId)
    },
    async loadPracticeDetail(practiceId) {
      if (!practiceId) return
      const requestId = ++this.detailRequestId
      this.detailLoading = true
      this.detailError = ''
      try {
        const result = await apiPost('/api/pronunciation-practice/detail', { practiceId })
        if (requestId !== this.detailRequestId) return
        this.detailPracticeId = practiceId
        this.selectedPracticeDetail = result
      } catch (error) {
        if (requestId !== this.detailRequestId) return
        if (/not found/i.test(error.message || '')) {
          this.records = this.records.filter((record) => record.practiceId !== practiceId)
          this.selectedPracticeId = this.records[0]?.practiceId || null
          this.actionError = this.copy.audioUnavailable
          if (this.selectedPracticeId) this.loadPracticeDetail(this.selectedPracticeId)
          return
        }
        this.detailError = error.message || 'Failed to load learning record detail'
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
      }
    },
    async openPractice(practice) {
      if (!practice || this.openingPracticeId) return

      this.selectedPracticeId = practice.practiceId
      this.openingPracticeId = practice.practiceId
      this.actionError = ''
      try {
        const audio = await resolveStudySource(practice, this.materialTranslate())
        this._handleViewChange('AudioPlayer', {
          ...audio,
          useSmartSplit: false,
          isDemo: false,
          returnView: 'LearningRecords',
          returnParams: {},
        })
      } catch (error) {
        if (error instanceof StudySourceMissingError) {
          await this.removeMissingPractice(practice)
          this.actionError = this.copy.audioUnavailable
        } else if (error instanceof StudySourceNotReadyError) {
          this.actionError = this.copy.openFailed
        } else {
          this.actionError = error.message || this.copy.openFailed
        }
      } finally {
        this.openingPracticeId = null
      }
    },
    materialTranslate() {
      const translate = (...args) => this.$t(...args)
      translate.locale = this.$i18n?.locale
      return translate
    },
    async removeMissingPractice(practice) {
      try {
        await apiPost('/api/pronunciation-practice/delete', { practiceId: practice.practiceId })
      } catch (error) {
        if (!/not found/i.test(error.message || '')) throw error
      }
      await this.loadRecords()
    },
    requestDelete(practice) {
      this.actionError = ''
      this.practiceToDelete = practice
    },
    cancelDelete() {
      if (!this.deleting) this.practiceToDelete = null
    },
    async confirmDelete() {
      if (!this.practiceToDelete || this.deleting) return
      const practiceId = this.practiceToDelete.practiceId
      this.deleting = true
      this.actionError = ''
      try {
        await apiPost('/api/pronunciation-practice/delete', { practiceId })
        this.practiceToDelete = null
        await this.loadRecords()
      } catch (error) {
        this.actionError = error.message || this.copy.deleteFailed
      } finally {
        this.deleting = false
      }
    },
    averageScore(practice) {
      return practice?.report?.latestAverageScores?.pronunciationScore ||
        practice?.stats?.averageScores?.pronunciationScore ||
        null
    },
    scoreLabel(value) {
      return Number.isFinite(Number(value)) ? String(Math.round(Number(value))) : '-'
    },
    scorePercent(value) {
      const score = Math.max(0, Math.min(100, Number(value) || 0))
      return `${score}%`
    },
    scoreDegrees(value) {
      const score = Math.max(0, Math.min(100, Number(value) || 0))
      return `${score * 3.6}deg`
    },
    scoreLevel(value) {
      const score = Number(value)
      if (!Number.isFinite(score)) return 'unknown'
      if (score >= 85) return 'excellent'
      if (score >= 70) return 'good'
      if (score >= 50) return 'needs-practice'
      return 'poor'
    },
    sourceLabel(practice) {
      return practice?.sourceType === 'material' ? this.copy.materialSource : this.copy.fileSource
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString()
    },
  },
}
</script>

<style scoped src="./LearningRecords.css"></style>
